import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Phone,
  ShoppingCart,
  Loader,
  AlertCircle,
  Wifi,
} from "lucide-react";
import { productsService } from "../services/productsService";
// import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import { formatMoney, parseMoney } from "../utils/money";

const AirtimeDataProducts = ({ onBack, walletBalances, onBalanceUpdate }) => {
  // const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [allProducts, setAllProducts] = useState([]);
  const [selectedNetwork, setSelectedNetwork] = useState("all");
  const [serviceType, setServiceType] = useState("airtime");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [purchasing, setPurchasing] = useState(null);

  // Airtime form
  const [airtimeNetwork, setAirtimeNetwork] = useState("");
  const [airtimeAmount, setAirtimeAmount] = useState("");

  const networks = [
    { value: "all", label: "All", icon: null },
    { value: "mtn", label: "MTN", icon: "/mtn.jpg" },
    { value: "telecel", label: "Telecel", icon: "/telecel.jpg" },
    { value: "airteltigo", label: "AirtelTigo", icon: "/at.jpg" },
  ];

  // Phone validation function
  const validatePhone = (phone) => {
    if (!phone) {
      return "Please enter a phone number";
    }

    if (!/^\+?[0-9\s\-()]+$/.test(phone)) {
      return "Phone number can only contain numbers and an optional leading +";
    }

    const cleanedPhone = phone.replace(/\D/g, "");
    if (cleanedPhone.length < 10) {
      return "Phone number must be at least 10 digits";
    }

    return null;
  };

  const isPhoneValid = () => {
    return validatePhone(recipientPhone) === null;
  };

  const normalizeText = (value) =>
    String(value || "")
      .trim()
      .toLowerCase();

  const normalizeNetwork = (value) => {
    const normalized = normalizeText(value).replace(/[\s_-]/g, "");

    if (normalized === "at") {
      return "airteltigo";
    }

    if (normalized === "airtel" || normalized === "tigo") {
      return "airteltigo";
    }

    return normalized;
  };

  const isAirtimeProduct = (product) => {
    const productType = normalizeText(
      product?.product_type || product?.type || product?.category,
    );
    const productName = normalizeText(product?.name);

    return (
      productType === "airtime" ||
      productType === "topup" ||
      productName.includes("airtime")
    );
  };

  const isDataProduct = (product) => {
    const productType = normalizeText(
      product?.product_type || product?.type || product?.category,
    );
    const productName = normalizeText(product?.name);

    return (
      productType === "data" ||
      productType === "bundle" ||
      Boolean(product?.data_amount) ||
      productName.includes("data")
    );
  };

  const getEffectiveAirtimeNetwork = () => {
    if (airtimeNetwork) {
      return normalizeNetwork(airtimeNetwork);
    }

    if (selectedNetwork !== "all") {
      return normalizeNetwork(selectedNetwork);
    }

    return "";
  };

  // Fetch products using new unified endpoint
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await productsService.getAirtimeDataProducts(
          selectedNetwork === "all" ? null : selectedNetwork,
        );

        if (response.status === "success") {
          const products = response.data.products || [];
          setAllProducts(products);
        } else {
          setError("Failed to load products");
        }
      } catch (err) {
        console.error("Failed to load products:", err);
        setError("Failed to load products");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [selectedNetwork]);

  useEffect(() => {
    if (selectedNetwork === "all") {
      return;
    }

    setAirtimeNetwork(selectedNetwork.toUpperCase());
  }, [selectedNetwork]);

  // Get airtime products for network selection
  const getAirtimeProducts = () => {
    return allProducts.filter((p) => isAirtimeProduct(p));
  };

  // Get data products
  const getDataProducts = () => {
    return allProducts.filter((p) => isDataProduct(p));
  };

  // Get network icon
  const getNetworkIcon = (networkValue) => {
    const network = networks.find((n) => n.value === networkValue);
    return network?.icon;
  };

  const handleAirtimePurchase = async () => {
    setError("");

    const phoneError = validatePhone(recipientPhone);
    if (phoneError) {
      setError(phoneError);
      return;
    }

    const effectiveNetwork = getEffectiveAirtimeNetwork();

    if (!effectiveNetwork || !airtimeAmount) {
      setError("Please fill in all fields");
      return;
    }

    // Find the airtime product for selected network
    const airtimeProduct = allProducts.find(
      (p) =>
        isAirtimeProduct(p) &&
        normalizeNetwork(p.network) === effectiveNetwork,
    );

    if (!airtimeProduct) {
      setError("Airtime product not found for selected network");
      return;
    }

    setPurchasing(`airtime_${airtimeProduct.id}`);

    try {
      const walletBalance = parseMoney(walletBalances?.airtime_data_balance);
      const amount = parseMoney(airtimeAmount);

      if (walletBalance < amount) {
        setError(`Insufficient balance. Need ₵${formatMoney(amount)}`);
        setPurchasing(null);
        return;
      }

      const cleanedPhone = recipientPhone.replace(/\D/g, "");

      // Use unified purchase endpoint
      const purchaseData = {
        product_id: airtimeProduct.id, // Disguised ID like prod_10_86c81d60
        wallet_type: "airtime_data",
        recipient_phone: cleanedPhone,
        amount: amount,
        // Include external product fields if available
        bundle_code: airtimeProduct.external_bundles?.[0]?.BundleCode || "",
        bundle_type: "FLEXI", // Airtime is typically flexi
      };

      const response = await productsService.purchaseProduct(purchaseData);

      if (response.status === "success") {
        toast.success(`₵${formatMoney(amount)} airtime sent to ${recipientPhone}!`);
        setAirtimeAmount("");
        if (onBalanceUpdate) onBalanceUpdate();
      } else {
        setError(response.message || "Purchase failed");
      }
    } catch (err) {
      console.error("Airtime purchase error:", err);
      setError(err.response?.data?.message || "Purchase failed");
    } finally {
      setPurchasing(null);
    }
  };

  const handleDataPurchase = async (product, customAmount = null) => {
    setError("");

    const phoneError = validatePhone(recipientPhone);
    if (phoneError) {
      setError(phoneError);
      return;
    }

    setPurchasing(product.id);

    try {
      const walletBalance = parseMoney(walletBalances?.airtime_data_balance);
      const amount =
        customAmount !== null ? parseMoney(customAmount) : parseMoney(product.price);

      if (walletBalance < amount) {
        setError(`Insufficient balance. Need ₵${formatMoney(amount)}`);
        setPurchasing(null);
        return;
      }

      const cleanedPhone = recipientPhone.replace(/\D/g, "");

      // Use unified purchase endpoint
      const purchaseData = {
        product_id: product.id, // Disguised ID like prod_11_abc123
        wallet_type: "airtime_data",
        recipient_phone: cleanedPhone,
        amount: amount,
        // Include external product fields
        bundle_code:
          product.external_bundles?.[0]?.BundleCode || product.external_code,
        bundle_type: product.external_bundles?.[0]?.BundleType || "FIXED",
      };

      const response = await productsService.purchaseProduct(purchaseData);

      if (response.status === "success") {
        toast.success(`Data bundle sent to ${recipientPhone}!`);
        if (onBalanceUpdate) onBalanceUpdate();
      } else {
        setError(response.message || "Purchase failed");
      }
    } catch (err) {
      console.error("Data purchase error:", err);
      setError(err.response?.data?.message || "Purchase failed");
    } finally {
      setPurchasing(null);
    }
  };

  // Handle flexi data purchase with custom amount
  const handleFlexiDataPurchase = async (product) => {
    const customAmount = prompt(
      `Enter amount between ₵${product.min_value || 1} and ₵${
        product.max_value || 1000
      }:`,
    );

    if (!customAmount) return;

    const amount = parseMoney(customAmount);
    const minVal = parseMoney(product.min_value || 1);
    const maxVal = parseMoney(product.max_value || 1000);

    if (amount < minVal || amount > maxVal) {
      setError(`Amount must be between ₵${formatMoney(minVal)} and ₵${formatMoney(maxVal)}`);
      return;
    }

    await handleDataPurchase(product, amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={onBack}
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
              Airtime & Data
            </h1>
            <p className="text-sm text-gray-500">Business packages</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs sm:text-sm text-gray-500">Balance</p>
          <p className="text-sm sm:text-lg font-semibold text-gray-900">
            ₵{formatMoney(walletBalances?.airtime_data_balance)}
          </p>
        </div>
      </div>

      {/* Network Filter */}
      <div className="flex space-x-2 overflow-x-auto pb-2">
        {networks.map((network) => (
          <button
            key={network.value}
            onClick={() => setSelectedNetwork(network.value)}
            className={`flex-shrink-0 flex items-center space-x-2 px-3 py-2 rounded-lg border transition-colors ${
              selectedNetwork === network.value
                ? "border-gray-900 bg-gray-900 text-white"
                : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
            }`}
          >
            {network.icon && (
              <img
                src={network.icon}
                alt={network.label}
                className="w-4 h-4 object-contain rounded"
              />
            )}
            <span className="text-sm font-medium">{network.label}</span>
          </button>
        ))}
      </div>

      {/* Service Type Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => {
            setServiceType("airtime");
            setError("");
          }}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            serviceType === "airtime"
              ? "border-gray-900 text-gray-900"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          Airtime
        </button>
        <button
          onClick={() => {
            setServiceType("data");
            setError("");
          }}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            serviceType === "data"
              ? "border-gray-900 text-gray-900"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          Data Bundles
        </button>
      </div>

      {/* Recipient Phone */}
      {serviceType && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Recipient Phone Number
          </label>
          <div className="relative">
            <Phone className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <input
              type="tel"
              value={recipientPhone}
              onChange={(e) => {
                setRecipientPhone(e.target.value);
                setError("");
              }}
              placeholder="024 123 4567"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent outline-none"
            />
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Airtime Section */}
      {serviceType === "airtime" && (
        <div className="space-y-4">
          <h3 className="font-medium text-gray-900">Select Network & Amount</h3>

          {/* Network Selection for Airtime */}
          <div className="grid grid-cols-3 gap-3">
            {getAirtimeProducts().map((product) => (
              <button
                key={product.id}
                onClick={() => setAirtimeNetwork(product.network.toUpperCase())}
                className={`flex items-center justify-center space-x-2 p-3 border-2 rounded-lg transition-all ${
                  airtimeNetwork === product.network.toUpperCase()
                    ? "border-gray-900 bg-gray-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                {getNetworkIcon(product.network) && (
                  <img
                    src={getNetworkIcon(product.network)}
                    alt={product.network}
                    className="w-5 h-5 object-contain"
                  />
                )}
                <span className="text-sm font-medium capitalize">
                  {product.network}
                </span>
              </button>
            ))}
          </div>

          {/* Amount Input */}
          <div>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {[1, 5, 10, 20].map((amount) => (
                <button
                  key={amount}
                  onClick={() => setAirtimeAmount(amount.toString())}
                  className={`py-2 px-3 border rounded-lg text-sm font-medium transition-colors ${
                    airtimeAmount === amount.toString()
                      ? "border-gray-900 bg-gray-900 text-white"
                      : "border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  ₵{amount}
                </button>
              ))}
            </div>
            <input
              type="number"
              value={airtimeAmount}
              onChange={(e) => setAirtimeAmount(e.target.value)}
              placeholder="Enter custom amount"
              min="0.5"
              max="1000"
              step="0.01"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent outline-none"
            />
            <p className="text-xs text-gray-500 mt-1">Min: ₵0.50, Max: ₵1000</p>
          </div>

          <button
            onClick={handleAirtimePurchase}
            disabled={
              purchasing ||
              !getEffectiveAirtimeNetwork() ||
              !isPhoneValid() ||
              !airtimeAmount
            }
            className="w-full bg-gray-900 hover:bg-gray-800 text-white py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {purchasing ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <ShoppingCart className="w-4 h-4" />
                <span>Purchase Airtime</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Data Bundles Section */}
      {serviceType === "data" && (
        <div className="space-y-4">
          {getDataProducts().length > 0 ? (
            <div className="space-y-3">
              {getDataProducts().map((product) => (
                <div
                  key={product.id}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1">
                      <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center">
                        <Wifi className="w-5 h-5 text-gray-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-gray-900">
                          {product.data_amount || product.name}
                        </div>
                        <div className="flex items-center space-x-2 mt-1">
                          {getNetworkIcon(product.network) && (
                            <img
                              src={getNetworkIcon(product.network)}
                              alt={product.network}
                              className="w-4 h-4 object-contain rounded"
                            />
                          )}
                          <span className="text-sm text-gray-500 uppercase font-medium">
                            {product.network}
                          </span>
                          {product.validity && (
                            <span className="text-xs text-gray-400">
                              • {product.validity}
                            </span>
                          )}
                        </div>
                        {/* Show price range for flexi bundles */}
                        {product.min_value && product.max_value && (
                          <div className="text-xs text-gray-500 mt-1">
                            Range: ₵{formatMoney(product.min_value)} - ₵
                            {formatMoney(product.max_value)}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="text-right">
                        <div className="font-bold text-gray-900">
                          {product.min_value && product.max_value
                            ? `₵${formatMoney(product.min_value)}-${formatMoney(product.max_value)}`
                            : `₵${formatMoney(product.price)}`}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          if (product.min_value && product.max_value) {
                            handleFlexiDataPurchase(product);
                          } else {
                            handleDataPurchase(product);
                          }
                        }}
                        disabled={purchasing === product.id || !isPhoneValid()}
                        className="bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium py-2 px-3 sm:px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        {purchasing === product.id ? (
                          <Loader className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <ShoppingCart className="w-4 h-4" />
                            <span>Buy</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Wifi className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="font-medium text-gray-900 mb-1">
                No data bundles found
              </h3>
              <p className="text-gray-500">Try selecting a different network</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AirtimeDataProducts;
